import moment from 'moment';

import { VALID_DATE_INPUT_FORMATS } from './constants';
import type { DateRangePreset } from './types';

/**
 * Result of parsing free-text typed into the date range picker.
 *
 * - `preset`: a relative range such as "last 7 days" or "30m". The picker turns
 *   this into concrete dates via `presetStartDate`, so we only need the shape.
 * - `range`: an absolute range such as "Jan 1 - Jan 7" (or a single day).
 */
export type ParsedDateRangeInput =
  | { type: 'preset'; preset: DateRangePreset }
  | { type: 'range'; startDate: Date; endDate: Date };

// Maps every accepted unit spelling to a moment duration unit. Order doesn't
// matter here because we look up the whole token, not a prefix.
const UNIT_ALIASES: Record<string, moment.DurationInputArg2> = {
  m: 'minutes',
  min: 'minutes',
  mins: 'minutes',
  minute: 'minutes',
  minutes: 'minutes',
  h: 'hours',
  hr: 'hours',
  hrs: 'hours',
  hour: 'hours',
  hours: 'hours',
  d: 'days',
  day: 'days',
  days: 'days',
  w: 'weeks',
  wk: 'weeks',
  wks: 'weeks',
  week: 'weeks',
  weeks: 'weeks',
  mo: 'months',
  mos: 'months',
  mon: 'months',
  month: 'months',
  months: 'months',
  y: 'years',
  yr: 'years',
  yrs: 'years',
  year: 'years',
  years: 'years',
};

// Separators that split an absolute range into start/end. Spaces around the
// dash are required so we don't break dates like "01-07-2026".
const RANGE_SEPARATORS = [' - ', ' – ', ' to ', ' — '];

const parseAbsoluteDate = (value: string): Date | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const format = VALID_DATE_INPUT_FORMATS.find((f) => moment(trimmed, f, true).isValid());
  return format ? moment(trimmed, format).toDate() : null;
};

const parseRelative = (input: string): DateRangePreset | null => {
  // Strip a leading "last"/"past"/"previous" and a trailing "ago" so phrases
  // like "last 7 days" and "7 days ago" both reduce to "<qty> <unit>".
  const normalized = input
    .replace(/^(last|past|previous)\s+/i, '')
    .replace(/\s+ago$/i, '')
    .trim();

  // Matches "7 days", "7days", "30m", and "week" (quantity defaults to 1).
  const match = normalized.match(/^(\d+)?\s*([a-z]+)$/i);
  if (!match) {
    return null;
  }

  const [, rawQuantity, rawUnit] = match;
  const unit = UNIT_ALIASES[rawUnit.toLowerCase()];
  if (!unit) {
    return null;
  }

  const quantity = rawQuantity ? Number(rawQuantity) : 1;
  if (quantity <= 0) {
    return null;
  }

  return { quantity, unit };
};

const parseAbsoluteRange = (input: string): { startDate: Date; endDate: Date } | null => {
  const separator = RANGE_SEPARATORS.find((sep) => input.includes(sep));

  if (separator) {
    const [rawStart, rawEnd, ...rest] = input.split(separator);
    if (rest.length > 0) {
      return null;
    }
    const start = parseAbsoluteDate(rawStart);
    const end = parseAbsoluteDate(rawEnd);
    if (!start || !end) {
      return null;
    }
    const startDate = moment(start).startOf('day').toDate();
    const endDate = moment(end).endOf('day').toDate();
    // Reject reversed ranges (e.g. "Jan 7 - Jan 1") rather than silently
    // committing a start that's after the end.
    if (startDate > endDate) {
      return null;
    }
    return { startDate, endDate };
  }

  // No separator: treat a single valid date as a one-day range.
  const single = parseAbsoluteDate(input);
  if (single) {
    return {
      startDate: moment(single).startOf('day').toDate(),
      endDate: moment(single).endOf('day').toDate(),
    };
  }

  return null;
};

/**
 * Parses free-text the user types into the date range picker into either a
 * relative preset or an absolute date range. Returns `null` when the text can't
 * be understood, so callers can surface an invalid-input state.
 *
 * Supported relative forms (optionally prefixed with "last"/"past"/"previous"
 * or suffixed with "ago"): "7 days", "7d", "30m", "24 hours", "2 weeks",
 * "3 months", "1 year", and the bare unit ("week" === "1 week").
 *
 * Supported absolute forms: any date in `VALID_DATE_INPUT_FORMATS`, either as a
 * single day ("Jan 7, 2026") or a range ("Jan 1 - Jan 7", "01/01 to 01/07").
 */
export const parseDateRangeInput = (rawInput: string): ParsedDateRangeInput | null => {
  const input = rawInput.trim();
  if (!input) {
    return null;
  }

  const preset = parseRelative(input);
  if (preset) {
    return { type: 'preset', preset };
  }

  const range = parseAbsoluteRange(input);
  if (range) {
    return { type: 'range', ...range };
  }

  return null;
};
